/**
 * StableRouteSystem.ts
 * DAoC-Authentic Stable-to-Stable Automated Horse Route System (마구간 ➔ 마구간 자동 가도 승마 시스템)
 *
 * - Players cannot freely steer mounts anywhere; instead, horses are boarded at a Stable Master (마구간지기)
 *   or by handing in a Route Ticket (마패) from the 20-slot inventory.
 * - Once boarded, the horse automatically follows road waypoints from Stable to Stable
 *   (including intra-zone stables in Solaris Hills and multi-zone BFS highway routes across all 7 Zones).
 * - Players can adjust observation speed (1x / 3x / 5x) or Jump Off (중도 하차) at any time during the ride.
 */

import { EntityState } from '../../core/Types';
import { EntityRegistry } from '../../core/EntityRegistry';
import { EventBus } from '../../core/EventBus';
import { Currency } from '../../core/Currency';

export interface StableNode {
  id: string;
  zoneId: number;
  zoneName: string;
  name: string;
  masterName: string;
  x: number;
  y: number;
  fareGold: number;
  description: string;
}

export interface RouteLeg {
  zoneId: number;
  zoneName: string;
  fromName: string;
  toName: string;
  waypoints: Array<{ x: number; y: number }>;
  nextZoneId?: number;
  nextZoneSpawn?: { x: number; y: number };
}

export const STABLE_NODES: StableNode[] = [
  // Zone 1: 솔라리스 언덕 (3 Intra-Zone Stables!)
  {
    id: 'stable_z1_crossroads',
    zoneId: 1,
    zoneName: 'Z1. 솔라리스 언덕',
    name: '솔라리스 중앙 십자로 마구간',
    masterName: '마구간지기 잭슨 (십자로 출장소)',
    x: 76,
    y: 83,
    fareGold: 100, // 100 코퍼
    description: '북·남·동 대가도와 왕도 솔라리움 성채로 갈라지는 중앙 교차로 마구간',
  },
  {
    id: 'stable_z1_oakhaven',
    zoneId: 1,
    zoneName: 'Z1. 솔라리스 언덕',
    name: '오크헤이븐 마을 마구간',
    masterName: '마구간지기 잭슨 (본점)',
    x: 105,
    y: 108,
    fareGold: 100, // 100 코퍼
    description: '오크헤이븐 초보자 거점 마을 입구의 왕실 공인 마구간',
  },
  {
    id: 'stable_z1_solarium',
    zoneId: 1,
    zoneName: 'Z1. 솔라리스 언덕',
    name: '왕도 솔라리움 대성문 마구간',
    masterName: '왕실 마구간장 베르나르',
    x: 50,
    y: 57,
    fareGold: 100, // 100 코퍼
    description: '솔라리움 성채 도개교 앞 광장에 위치한 왕실 직속 마구간',
  },
  // Zone 2: 그란델 평원
  {
    id: 'stable_z2_windmill',
    zoneId: 2,
    zoneName: 'Z2. 그란델 평원',
    name: '윈드밀 타운 풍차마을 마구간',
    masterName: '마구간지기 한스 (Hans)',
    x: 114,
    y: 78,
    fareGold: 100, // 100 코퍼
    description: '크롬레크 거석 유적 동쪽 황금 밀밭 지대의 중심 마구간',
  },
  // Zone 3: 실바나 대삼림
  {
    id: 'stable_z3_lakewood',
    zoneId: 3,
    zoneName: 'Z3. 실바나 대삼림',
    name: '레이크우드 호수마을 마구간',
    masterName: '마구간지기 엘로윈 (Elowen)',
    x: 84,
    y: 118,
    fareGold: 100, // 100 코퍼
    description: '신성한 실바나 호수 남쪽 숲길에 자리한 엘프 마구간',
  },
  // Zone 4: 보레아스 설원
  {
    id: 'stable_z4_ironfrost',
    zoneId: 4,
    zoneName: 'Z4. 보레아스 설원',
    name: '아이언프로스트 요새 마구간',
    masterName: '마구간지기 토르그 (Torg)',
    x: 76,
    y: 128,
    fareGold: 100, // 100 코퍼
    description: '만년설 빙하 지대 입구를 지키는 북방 전초기지 마구간',
  },
  // Zone 5: 네뷸라 늪지
  {
    id: 'stable_z5_reedwater',
    zoneId: 5,
    zoneName: 'Z5. 네뷸라 늪지',
    name: '리드워터 수상촌 마구간',
    masterName: '마구간지기 카르가 (Karga)',
    x: 76,
    y: 84,
    fareGold: 100, // 100 코퍼
    description: '루미나 차원문 항구와 연결된 판자 다리 교차로 마구간',
  },
  // Zone 6: 이그니스 황무지
  {
    id: 'stable_z6_mirage',
    zoneId: 6,
    zoneName: 'Z6. 이그니스 황무지',
    name: '미라지 오아시스 캠프 마구간',
    masterName: '마구간지기 자파르 (Jafar)',
    x: 76,
    y: 42,
    fareGold: 100, // 100 코퍼
    description: '불의 용 이그나르 화산 분화구 북쪽 오아시스 마구간',
  },
  // Zone 7: 루미나 성역섬
  {
    id: 'stable_z7_lumina',
    zoneId: 7,
    zoneName: 'Z7. 루미나 성역섬',
    name: '루미나 수정도시 성역 마구간',
    masterName: '마구간지기 루미엘 (Lumiel)',
    x: 76,
    y: 118,
    fareGold: 100, // 100 코퍼
    description: '바란 경의 성채 남쪽 신성 크리스탈 광장 마구간',
  },
];

// Zone adjacency graph for multi-zone BFS routing
const ZONE_ADJACENCY: Record<number, number[]> = {
  1: [2, 3, 4],
  2: [1, 5],
  3: [1, 5, 6],
  4: [1],
  5: [2, 3, 7],
  6: [3],
  7: [5],
};

export class StableRouteSystem {
  private registry: EntityRegistry;
  private getCurrentZoneId: () => number;
  private onZoneTransition: (targetZoneId: number, spawnX: number, spawnY: number) => void;
  private onStateChanged: () => void;

  private active = false;
  private legs: RouteLeg[] = [];
  private currentLegIndex = 0;
  private currentWaypointIndex = 0;
  private speedMultiplier = 1.0; // 1x (Base Horse Gallop = 16 tiles/s), 2x, 4x
  private departureStableName = '';
  private destinationStableName = '';

  constructor(
    registry: EntityRegistry,
    getCurrentZoneId: () => number,
    onZoneTransition: (targetZoneId: number, spawnX: number, spawnY: number) => void,
    onStateChanged: () => void
  ) {
    this.registry = registry;
    this.getCurrentZoneId = getCurrentZoneId;
    this.onZoneTransition = onZoneTransition;
    this.onStateChanged = onStateChanged;
  }

  public isRiding(): boolean {
    return this.active;
  }

  public getSpeedMultiplier(): number {
    return this.speedMultiplier;
  }

  public setSpeedMultiplier(mult: number): void {
    this.speedMultiplier = mult;
    this.onStateChanged();
  }

  public getActiveLeg(): RouteLeg | null {
    if (!this.active || this.currentLegIndex >= this.legs.length) return null;
    return this.legs[this.currentLegIndex];
  }

  public getRouteStatus(): {
    active: boolean;
    fromName: string;
    toName: string;
    legIndex: number;
    totalLegs: number;
    progressPct: number;
    speedMult: number;
    remainingWaypoints: Array<{ x: number; y: number }>;
  } {
    const leg = this.getActiveLeg();
    if (!this.active || !leg) {
      return {
        active: false,
        fromName: '',
        toName: '',
        legIndex: 0,
        totalLegs: 0,
        progressPct: 0,
        speedMult: this.speedMultiplier,
        remainingWaypoints: [],
      };
    }

    const totalWps = this.legs.reduce((acc, l) => acc + l.waypoints.length, 0);
    let completedWps = 0;
    for (let i = 0; i < this.currentLegIndex; i++) {
      completedWps += this.legs[i].waypoints.length;
    }
    completedWps += this.currentWaypointIndex;
    const progressPct = Math.min(99, Math.round((completedWps / Math.max(1, totalWps)) * 100));

    return {
      active: true,
      fromName: this.departureStableName,
      toName: this.destinationStableName,
      legIndex: this.currentLegIndex + 1,
      totalLegs: this.legs.length,
      progressPct,
      speedMult: this.speedMultiplier,
      remainingWaypoints: leg.waypoints.slice(this.currentWaypointIndex),
    };
  }

  /**
   * Finds the nearest Stable in the current Zone to the player's position.
   */
  public getNearestStableInCurrentZone(px: number, py: number): StableNode {
    const curZ = this.getCurrentZoneId();
    const candidates = STABLE_NODES.filter((s) => s.zoneId === curZ);
    if (candidates.length === 0) return STABLE_NODES[0];
    let best = candidates[0];
    let bestDist = Math.hypot(px - best.x, py - best.y);
    for (const c of candidates) {
      const d = Math.hypot(px - c.x, py - c.y);
      if (d < bestDist) {
        best = c;
        bestDist = d;
      }
    }
    return best;
  }

  /**
   * Starts an automated Stable-to-Stable horse route from `fromStableId` to `toStableId`.
   * When the Party Leader rides a horse, ALL party members mount their horses too,
   * and the Party Leader pays 100% of the horse fare for the entire party (`fareGold * party.length`),
   * unless `alreadyPaidByTicket = true` (when handing over a pre-purchased Horse Route Ticket).
   */
  public startRoute(fromStableId: string, toStableId: string, alreadyPaidByTicket = false): boolean {
    const fromNode = STABLE_NODES.find((s) => s.id === fromStableId);
    const toNode = STABLE_NODES.find((s) => s.id === toStableId);
    if (!fromNode || !toNode) return false;

    if (fromNode.id === toNode.id) {
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `⚠️ [말 상인 안내] 이미 [${toNode.name}] 에 위치해 있습니다. 다른 목적지 마구간을 선택하세요!`,
        category: 'system',
      });
      return false;
    }

    const player = this.registry.getPlayer();
    if (!player) return false;

    const party = this.registry.getParty();
    const partySize = Math.max(1, party.length);
    const totalPartyFare = toNode.fareGold * partySize;

    // If not handing over an already-purchased ticket, Party Leader pays full fare for all party members
    if (!alreadyPaidByTicket) {
      const leaderGold = player.gold ?? Currency.toCopper({ silver: 1 });
      if (leaderGold < totalPartyFare) {
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: `⚠️ [말 값 부족] 파티 리더 [${player.name}]의 소지금(${Currency.format(leaderGold)})이 부족합니다! (${partySize}인 파티 전원 말 값 총액: ${Currency.format(totalPartyFare)} = 1인당 ${Currency.format(toNode.fareGold)} × ${partySize}명)`,
          category: 'system',
        });
        return false;
      }
      player.gold = leaderGold - totalPartyFare;
    }

    // Ensure party is in departure stable's zone & position
    if (this.getCurrentZoneId() !== fromNode.zoneId) {
      this.onZoneTransition(fromNode.zoneId, fromNode.x, fromNode.y);
    } else {
      this.movePartyToPoint(fromNode.x, fromNode.y);
    }

    // Build road waypoint legs from `fromNode` to `toNode`
    this.legs = this.buildRouteLegs(fromNode, toNode);
    this.currentLegIndex = 0;
    this.currentWaypointIndex = 0;
    this.departureStableName = fromNode.name;
    this.destinationStableName = toNode.name;
    this.active = true;

    // Party Leader mounts horse -> ALL party members automatically mount their own horses!
    for (const member of party) {
      member.isMounted = true;
      member.targetId = null;
      member.cast = null;
    }

    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: performance.now(),
      text: alreadyPaidByTicket
        ? `🎫🐎 [말 상인에게 승마표 전달 완료!] 파티 리더 [${player.name}]님이 말 상인에게 표를 건네주어 파티 ${partySize}인 전원이 각자 말을 타고 [${fromNode.name}] ➔ [${toNode.name}] 가도 자동 주행을 시작합니다! (중도 하차: Space)`
        : `💰🐎 [파티 리더 말 값 전액 부담 & 전원 승마 출발!] 파티 리더 [${player.name}]님이 ${partySize}인분 말 값 총 ${Currency.format(totalPartyFare)}(1인당 ${Currency.format(toNode.fareGold)} × ${partySize}명)를 지불하고 표를 말 상인에게 건네주어, 파티 전원이 말을 타고 [${fromNode.name}] ➔ [${toNode.name}]로 출발합니다!`,
      category: 'system',
    });

    this.onStateChanged();
    return true;
  }

  /**
   * Emergency / voluntary dismount mid-route (DAoC Jump Off horse)
   */
  public dismountMidRoute(reason = '자발적 중도 하차'): void {
    if (!this.active) return;
    this.active = false;
    this.legs = [];
    for (const member of this.registry.getParty()) {
      member.isMounted = false;
      member.vx = 0;
      member.vy = 0;
    }

    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: performance.now(),
      text: `🚶 [승마 중도 하차] 말에서 뛰어내려 현재 도로 위치에서 도보 이동으로 전환했습니다. (${reason})`,
      category: 'system',
    });
    this.onStateChanged();
  }

  /**
   * Frame update: Moves the mounted party along the current road waypoint leg.
   */
  public update(dt: number): void {
    if (!this.active) return;
    const leg = this.getActiveLeg();
    if (!leg) {
      this.completeRoute();
      return;
    }

    const player = this.registry.getPlayer();
    if (!player) return;

    const targetWp = leg.waypoints[this.currentWaypointIndex];
    if (!targetWp) {
      // Leg finished! Check if we transition to next Zone or complete route
      if (leg.nextZoneId && leg.nextZoneSpawn) {
        this.onZoneTransition(leg.nextZoneId, leg.nextZoneSpawn.x, leg.nextZoneSpawn.y);
        // Re-ensure party stays mounted after zone transition
        for (const member of this.registry.getParty()) {
          member.isMounted = true;
        }
        this.currentLegIndex++;
        this.currentWaypointIndex = 0;
        this.onStateChanged();
        return;
      } else {
        this.completeRoute();
        return;
      }
    }

    const dx = targetWp.x - player.x;
    const dy = targetWp.y - player.y;
    const dist = Math.hypot(dx, dy);

    // Base Stable Horse speed = 15.0 tiles/sec * speedMultiplier
    const horseSpeed = 15.0 * this.speedMultiplier;
    const step = horseSpeed * dt;

    if (dist <= Math.max(0.45, step)) {
      player.x = targetWp.x;
      player.y = targetWp.y;
      this.currentWaypointIndex++;
      this.updateCompanionFormation(player);
    } else {
      const nx = dx / dist;
      const ny = dy / dist;
      player.vx = nx * horseSpeed;
      player.vy = ny * horseSpeed;
      player.x += player.vx * dt;
      player.y += player.vy * dt;
      player.dir = this.vectorToDir8(nx, ny);
      this.updateCompanionFormation(player);
    }
  }

  private completeRoute(): void {
    this.active = false;
    const destName = this.destinationStableName;
    this.legs = [];

    for (const member of this.registry.getParty()) {
      member.isMounted = false;
      member.vx = 0;
      member.vy = 0;
    }

    EventBus.emit('SPELL_EFFECT_TRIGGERED', {
      type: 'heal',
      gx: this.registry.getPlayer()?.x || 80,
      gy: this.registry.getPlayer()?.y || 80,
      radius: 2.0,
    });

    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: performance.now(),
      text: `🏁 [마구간 도착 완료!] 목적지 [${destName}] 에 안전하게 도착하여 말에서 내렸습니다!`,
      category: 'system',
    });
    this.onStateChanged();
  }

  private movePartyToPoint(x: number, y: number): void {
    const leader = this.registry.getPlayer();
    if (!leader) return;
    leader.x = x;
    leader.y = y;
    this.updateCompanionFormation(leader);
  }

  private updateCompanionFormation(leader: EntityState): void {
    const offsets = [
      { dx: -0.9, dy: -0.7 },
      { dx: 0.9, dy: -0.7 },
      { dx: -1.4, dy: -1.4 },
      { dx: 0.0, dy: -1.5 },
      { dx: 1.4, dy: -1.4 },
    ];
    let idx = 0;
    for (const m of this.registry.getParty()) {
      if (m.id === leader.id) continue;
      const off = offsets[idx % offsets.length];
      m.x = leader.x + off.dx;
      m.y = leader.y + off.dy;
      m.vx = leader.vx;
      m.vy = leader.vy;
      m.dir = leader.dir;
      m.isMounted = leader.isMounted;
      idx++;
    }
  }

  private vectorToDir8(nx: number, ny: number): EntityState['dir'] {
    const ang = Math.atan2(ny, nx) * (180 / Math.PI);
    if (ang >= -22.5 && ang < 22.5) return 'SE';
    if (ang >= 22.5 && ang < 67.5) return 'S';
    if (ang >= 67.5 && ang < 112.5) return 'SW';
    if (ang >= 112.5 && ang < 157.5) return 'W';
    if (ang >= -67.5 && ang < -22.5) return 'E';
    if (ang >= -112.5 && ang < -67.5) return 'NE';
    if (ang >= -157.5 && ang < -112.5) return 'N';
    return 'NW';
  }

  /**
   * Builds highway waypoint legs between any two Stables (Intra-zone or Multi-zone BFS).
   */
  private buildRouteLegs(fromNode: StableNode, toNode: StableNode): RouteLeg[] {
    // Case A: Same Zone (e.g., Solaris Hills Intra-Zone Stables!)
    if (fromNode.zoneId === toNode.zoneId) {
      const wps = this.buildIntraZoneWaypoints(fromNode.zoneId, { x: fromNode.x, y: fromNode.y }, { x: toNode.x, y: toNode.y });
      return [
        {
          zoneId: fromNode.zoneId,
          zoneName: fromNode.zoneName,
          fromName: fromNode.name,
          toName: toNode.name,
          waypoints: wps,
        },
      ];
    }

    // Case B: Multi-Zone BFS Shortest Path across the 7 Terra Anima Zones
    const zonePath = this.findShortestZonePath(fromNode.zoneId, toNode.zoneId);
    const legs: RouteLeg[] = [];
    let currentStart = { x: fromNode.x, y: fromNode.y };

    for (let i = 0; i < zonePath.length; i++) {
      const zId = zonePath[i];
      const isLast = i === zonePath.length - 1;

      if (isLast) {
        const wps = this.buildIntraZoneWaypoints(zId, currentStart, { x: toNode.x, y: toNode.y });
        legs.push({
          zoneId: zId,
          zoneName: toNode.zoneName,
          fromName: `${toNode.zoneName} 국경 관문`,
          toName: toNode.name,
          waypoints: wps,
        });
      } else {
        const nextZ = zonePath[i + 1];
        const borderInfo = this.getZoneTransitionExit(zId, nextZ);
        const wps = this.buildIntraZoneWaypoints(zId, currentStart, { x: borderInfo.exitX, y: borderInfo.exitY });
        legs.push({
          zoneId: zId,
          zoneName: `Z${zId}`,
          fromName: i === 0 ? fromNode.name : `Z${zId} 진입로`,
          toName: `Z${nextZ} 행 국경 관문`,
          waypoints: wps,
          nextZoneId: nextZ,
          nextZoneSpawn: { x: borderInfo.spawnX, y: borderInfo.spawnY },
        });
        currentStart = { x: borderInfo.spawnX, y: borderInfo.spawnY };
      }
    }

    return legs;
  }

  private findShortestZonePath(startZone: number, endZone: number): number[] {
    const queue: number[][] = [[startZone]];
    const visited = new Set<number>([startZone]);
    while (queue.length > 0) {
      const path = queue.shift()!;
      const last = path[path.length - 1];
      if (last === endZone) return path;
      for (const nxt of ZONE_ADJACENCY[last] || []) {
        if (!visited.has(nxt)) {
          visited.add(nxt);
          queue.push([...path, nxt]);
        }
      }
    }
    return [startZone, endZone];
  }

  /**
   * Generates smooth highway waypoints via the Zone's central hub (80, 80) so horses stay on roads!
   */
  private buildIntraZoneWaypoints(
    zoneId: number,
    start: { x: number; y: number },
    end: { x: number; y: number }
  ): Array<{ x: number; y: number }> {
    const hub = { x: 80, y: 80 };
    const rawPoints: Array<{ x: number; y: number }> = [start];

    // Route through the central highway crossroads (80, 80) if start and end are on different branches
    const distStartHub = Math.hypot(start.x - hub.x, start.y - hub.y);
    const distEndHub = Math.hypot(end.x - hub.x, end.y - hub.y);
    const directDist = Math.hypot(end.x - start.x, end.y - start.y);

    if (distStartHub > 8 && distEndHub > 8 && directDist > 20) {
      // In Zone 1, Oakhaven road goes (105,108) -> (110, 80) -> (80, 80)
      if (zoneId === 1 && start.x > 95 && start.y > 95) {
        rawPoints.push({ x: 109, y: 95 }, { x: 109, y: 80 });
      }
      rawPoints.push(hub);
      if (zoneId === 1 && end.x > 95 && end.y > 95) {
        rawPoints.push({ x: 109, y: 80 }, { x: 109, y: 95 });
      }
    }

    rawPoints.push(end);

    // Subdivide segments every ~5 tiles for smooth road following & path rendering
    const subdivided: Array<{ x: number; y: number }> = [];
    for (let i = 0; i < rawPoints.length - 1; i++) {
      const a = rawPoints[i];
      const b = rawPoints[i + 1];
      const segDist = Math.hypot(b.x - a.x, b.y - a.y);
      const steps = Math.max(1, Math.ceil(segDist / 5));
      for (let s = 1; s <= steps; s++) {
        const t = s / steps;
        subdivided.push({
          x: Number((a.x + (b.x - a.x) * t).toFixed(1)),
          y: Number((a.y + (b.y - a.y) * t).toFixed(1)),
        });
      }
    }
    return subdivided;
  }

  /**
   * Returns highway border exit coordinates & arrival spawn coordinates between adjacent zones.
   * Uses a 38-tile highway run from Crossroads (80,80) so multi-zone rides are brisk and scenic!
   */
  private getZoneTransitionExit(
    fromZ: number,
    toZ: number
  ): { exitX: number; exitY: number; spawnX: number; spawnY: number } {
    if (fromZ === 1 && toZ === 4) return { exitX: 80, exitY: 42, spawnX: 80, spawnY: 118 };
    if (fromZ === 4 && toZ === 1) return { exitX: 80, exitY: 118, spawnX: 80, spawnY: 42 };

    if (fromZ === 1 && toZ === 3) return { exitX: 80, exitY: 118, spawnX: 80, spawnY: 42 };
    if (fromZ === 3 && toZ === 1) return { exitX: 80, exitY: 42, spawnX: 80, spawnY: 118 };

    if (fromZ === 1 && toZ === 2) return { exitX: 118, exitY: 80, spawnX: 42, spawnY: 80 };
    if (fromZ === 2 && toZ === 1) return { exitX: 42, exitY: 80, spawnX: 118, spawnY: 80 };

    if (fromZ === 2 && toZ === 5) return { exitX: 118, exitY: 118, spawnX: 80, spawnY: 42 };
    if (fromZ === 5 && toZ === 2) return { exitX: 80, exitY: 42, spawnX: 118, spawnY: 118 };

    if (fromZ === 3 && toZ === 5) return { exitX: 118, exitY: 80, spawnX: 42, spawnY: 80 };
    if (fromZ === 5 && toZ === 3) return { exitX: 42, exitY: 80, spawnX: 118, spawnY: 80 };

    if (fromZ === 3 && toZ === 6) return { exitX: 80, exitY: 118, spawnX: 80, spawnY: 42 };
    if (fromZ === 6 && toZ === 3) return { exitX: 80, exitY: 42, spawnX: 80, spawnY: 118 };

    if (fromZ === 5 && toZ === 7) return { exitX: 118, exitY: 80, spawnX: 80, spawnY: 80 };
    if (fromZ === 7 && toZ === 5) return { exitX: 67, exitY: 80, spawnX: 118, spawnY: 80 };

    return { exitX: 80, exitY: 60, spawnX: 80, spawnY: 80 };
  }
}
